import React from 'react';
import Tracking from '@vromo/analytics';

import styled from '@emotion/styled';

import ReactTelInput from '../../common/components/ControlledTelInput/ControlledTelInput';
import ProfilePictureEditor from './ProfilePictureEditor';
import Container from '@vromo/ui/components/Container';
import Input from '@vromo/ui/components/Input';
import Button from '@vromo/ui/components/Button';
import RadioGroup from '@vromo/ui/components/Radio';
import Alert from '@vromo/ui/components/Alert';
import { useToasts } from '@vromo/ui/components/Toasts';

import { observer, useLocalObservable } from 'mobx-react-lite';
import { Contact } from '@vromo/models';
import { useStore } from '@vromo/mobx-react';
import useViewModel from '@vromo/hooks/useViewModel';
import { RootStore } from '../../stores/RootStore';
import { update } from 'serializr';
import { action, runInAction } from 'mobx';
import { passwordCheck } from '@vromo/ui/helpers';

const FormGroup = styled.div`
    margin-bottom: 15px;    
`;

const Errors = observer(function Errors({ errors, field }) {
    return (
        <div>
            {errors
                .filter((error) => error.field === field)
                .map((error, ix) => (
                    <Alert key={ix} success={!error.error} danger={error.error}>
                        {error.message}
                    </Alert>
                ))}
        </div>
    );
});

export default observer(function UserSettings() {
    const {
        authStore: { graph, api, user, token },
        viewStore
    } = useStore<RootStore>('rootStore');

    const showToast = useToasts();

    const store = useLocalObservable<{
        errors: Array<any>;
        oldPassword: string;
        newPassword: string;
        confirmPassword: string;
        clearPasswords: () => void;
    }>(() => ({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
        errors: [],
        clearPasswords() {
            this.oldPassword = '';
            this.newPassword = '';
            this.confirmPassword = '';
        }
    }));

    const [contact, contactPatch] = useViewModel(user.contact || new Contact());

    function updateContact() {
        store.errors = store.errors.filter((error) => error.field !== 'contact');

        graph.root
            .graph()
            .user()
            .contact()
            .post(contactPatch)
            .then((patch) => {
                update(user.contact, { ...patch });

                store.errors.push({
                    field: 'contact',
                    error: false,
                    message: 'Contact details updated successfully'
                });

                Tracking.track('Contact Details Updated');
            })
            .catch((e) => {
                console.error(e);
                showToast('Something went wrong with updating your details. Try again.', 'error');
            });
    }

    async function updatePassword() {
        const { oldPassword, newPassword, confirmPassword } = store;

        store.errors = store.errors.filter((error) => error.field !== 'password');
        
        if (oldPassword && !oldPassword.length) {
            store.errors.push({
                field: 'password',
                error: true,
                message: 'Please enter your old password.'
            });
        }

        store.errors.push(...passwordCheck(newPassword, confirmPassword));

        if (store.errors.length) return;

        api.changePassword
            .post({ existing: oldPassword, password: newPassword }, undefined, token)
            .then(() => {
                runInAction(() => {
                    Tracking.track('Password Changed');
                    store.errors.push({
                        field: 'password',
                        error: false,
                        message: 'Password updated successfully.'
                    });

                    store.clearPasswords();
                });
            })
            .catch((e) => {
                const { response } = e;

                const error = { field: 'password', error: true, message: '' };

                if (response.status === 400) {
                    store.errors.push({
                        ...error,
                        message: 'Please choose a stronger password.'
                    });
                }
                if (response.status === 403) {
                    store.errors.push({
                        ...error,
                        message: 'Your old password was incorrect. Please try again.'
                    });
                }
            });
    }

    return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', width: '50vw', minWidth: 350 }}>
                <Container title="Profile Picture">
                    <ProfilePictureEditor email={contact.email} />
                </Container>

                <Container title="Contact Details">
                    <div>
                        <Errors errors={store.errors} field="contact" />

                        <FormGroup>
                            <Input
                                type="text"
                                label="Name"
                                placeholder="Name"
                                value={contact.name}
                                onChange={(e) => update(contact, { name: e.target.value })}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Input
                                type="text"
                                label="Email"
                                placeholder="Email"
                                value={contact.email}
                                onChange={(e) => update(contact, { email: e.target.value })}
                            />
                        </FormGroup>
                        <FormGroup>
                            <label>Phone</label>
                            <ReactTelInput
                                value={contact.phone || ''}
                                default={viewStore.callingCode}
                                defaultCountry={viewStore.country}
                                onChange={(phone: string) =>
                                    update(contact, { phone: phone.replace(/\s/, '') })
                                }
                            />
                        </FormGroup>
                        <FormGroup>
                            <Button onClick={updateContact}>Save</Button>
                        </FormGroup>
                    </div>
                </Container>
                <Container title="Distance Units">
                    <div>
                        <RadioGroup
                            options={[
                                {
                                    label: 'Metric',
                                    value: 'false'
                                },
                                { label: 'Imperial', value: 'true' }
                            ]}
                            direction="row"
                            onChange={action((e) => {
                                viewStore.imperialUnits = e.target.value === 'true';
                            })}
                            checked={viewStore.imperialUnits?.toString()}
                        />
                    </div>
                </Container>

                <Container title="Change Password">
                    <div>
                        <Errors errors={store.errors} field="password" />
                        <FormGroup>
                            <Input
                                type="password"
                                label="Old Password"
                                value={store.oldPassword}
                                onChange={(e) => (store.oldPassword = e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Input
                                type="password"
                                label="New Password"
                                value={store.newPassword}
                                onChange={(e) => (store.newPassword = e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Input
                                type="password"
                                label="Confirm Password"
                                value={store.confirmPassword}
                                onChange={(e) => (store.confirmPassword = e.target.value)}
                            />
                        </FormGroup>

                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button type="submit" onClick={updatePassword}>
                                Update
                            </Button>
                        </div>
                    </div>
                </Container>
            </div>
        </div>
    );
});
